import forEach from "lodash/forEach";
import {
  FORM_ROLES_KEY,
  FORM_SUBTEMPLATES_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useMultiActivityFormStoreActions } from "PFApp/stores/activity_form/multi_roles_form";
import {
  useManageRolesStore,
  useManageRolesStoreActions,
  useManageRolesStoreContext
} from "PFApp/stores/manage_roles";
import {
  ManageRolesFormType,
  ManageRolesMultiForm
} from "PFApp/types/activity_form/manage_roles_form_schema";
import { useInvalidateManageViewRoles } from "PFCore/hooks/queries/roles/use_invalidate_manage_view_roles";
import { Id } from "PFCore/types";
import { ManageRolesViewRoleItem } from "PFCore/types/manage_roles_view";
import { Profile } from "PFCore/types/profile";
import { MutableRefObject, PropsWithChildren, useCallback, useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useUnmount } from "react-use";

import { useManageRolesInitialRoleData } from "../hooks/use_manage_roles_initial_role_data";
import { useUnsavedEditsSync } from "../hooks/use_unsaved_edits_sync";
import { getStaleFieldErrorPaths, groupRolesIntoForm, mergeDraftRolesIntoForm } from "../manage_roles.utils";
import { applyPendingEditsToFormData } from "./manage_roles_form_provider.utils";
import { UnsavedEditsSyncContext } from "./unsaved_edits_sync_context";

type ManageRolesFormProviderProps = PropsWithChildren<{
  entries: ManageRolesViewRoleItem[];
}>;

type ManageRolesFormProviderInnerProps = PropsWithChildren<{
  currentPage: number;
  trackerResetVersionRef: MutableRefObject<number>;
}>;

const ManageRolesFormProviderInner = ({
  children,
  currentPage,
  trackerResetVersionRef
}: ManageRolesFormProviderInnerProps) => {
  const { invalidatePage, removeAll } = useInvalidateManageViewRoles();

  const { hasUnsavedEdits } = useManageRolesStoreActions();
  const requiredKeysRef = useRef<Map<number, Set<string>>>(new Map());
  const registerRequiredKeys = useCallback((subtemplateIndex: number, keys: Set<string>) => {
    requiredKeysRef.current.set(subtemplateIndex, keys);
  }, []);
  const unregisterRequiredKeys = useCallback((subtemplateIndex: number) => {
    requiredKeysRef.current.delete(subtemplateIndex);
  }, []);
  const trackedKeysRef = useRef<Map<number, Set<string>>>(new Map());
  const registerTrackedKeys = useCallback((subtemplateIndex: number, keys: Set<string>) => {
    trackedKeysRef.current.set(subtemplateIndex, keys);
  }, []);
  const unregisterTrackedKeys = useCallback((subtemplateIndex: number) => {
    trackedKeysRef.current.delete(subtemplateIndex);
  }, []);
  const getTrackedKeys = useCallback(
    (subtemplateIndex: number) => trackedKeysRef.current.get(subtemplateIndex),
    []
  );
  const { flushUnsavedEdits, pauseSync, resumeSync } = useUnsavedEditsSync({
    requiredKeysRef
  });

  const prevPageRef = useRef(currentPage);

  useEffect(() => {
    if (prevPageRef.current === currentPage) {
      return;
    }
    const previousPage = prevPageRef.current;
    prevPageRef.current = currentPage;
    invalidatePage(previousPage);
  }, [currentPage, invalidatePage]);

  // Remove (not just invalidate) the query cache on unmount so that re-entering the page
  // starts with a fresh fetch. Invalidation alone leaves stale data in the cache, which
  // react-query serves immediately on remount while refetching in the background. That
  // two-step form population (stale → fresh) causes the unsaved edits tracker to
  // misinterpret the data refresh as a user edit, firing a duplicate update request.
  useUnmount(removeAll);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedEdits()) {
        // Shows native "Leave page?" dialog. If the user confirms, unsaved edits will be lost.
        // Async flushing is not viable here — the browser does not await promises in beforeunload,
        // and navigator.sendBeacon only supports POST without custom auth headers.
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedEdits]);

  const contextValue = useMemo(
    () => ({
      flushUnsavedEdits,
      pauseSync,
      resumeSync,
      trackerResetVersionRef,
      registerRequiredKeys,
      unregisterRequiredKeys,
      registerTrackedKeys,
      unregisterTrackedKeys,
      getTrackedKeys
    }),
    [
      flushUnsavedEdits,
      pauseSync,
      resumeSync,
      trackerResetVersionRef,
      registerRequiredKeys,
      unregisterRequiredKeys,
      registerTrackedKeys,
      unregisterTrackedKeys,
      getTrackedKeys
    ]
  );

  return <UnsavedEditsSyncContext.Provider value={contextValue}>{children}</UnsavedEditsSyncContext.Provider>;
};

export const ManageRolesFormProvider = ({ children, entries }: ManageRolesFormProviderProps) => {
  const { setRoleData, clearRoleData } = useMultiActivityFormStoreActions();
  const { getInitialRoleData } = useManageRolesInitialRoleData();
  const { removeNewDraftRole } = useManageRolesStoreActions();
  const manageRolesStoreApi = useManageRolesStoreContext();

  const currentPage = useManageRolesStore((state) => state.currentPage);
  const newDraftRoles = useManageRolesStore((state) => state.newDraftRoles);

  const methods = useForm<ManageRolesMultiForm>({
    reValidateMode: "onSubmit"
  });

  const profileByUniqueIdRef = useRef<Map<string, Profile | undefined>>(new Map());
  const newDraftRolesRef = useRef(newDraftRoles);
  newDraftRolesRef.current = newDraftRoles;
  // Bumped on every programmatic form reset so the unsaved-edits trackers re-baseline
  // instead of misreading the server-driven repopulation as user edits.
  const trackerResetVersionRef = useRef(0);

  const { formData, profileByUniqueId } = useMemo(() => {
    const formEntries = entries.map(getInitialRoleData);
    const profileMap = new Map(formEntries.map((formRole, i) => [formRole.uniqueId, entries[i].profile]));
    return { formData: groupRolesIntoForm(formEntries), profileByUniqueId: profileMap };
  }, [entries, getInitialRoleData]);

  profileByUniqueIdRef.current = profileByUniqueId;

  const syncFormPage = useCallback(
    (serverFormData: ManageRolesMultiForm) => {
      const previousValues = methods.getValues();
      const draftRoles = newDraftRolesRef.current;

      const previousRolesByUniqueId = new Map<string, ManageRolesFormType>();
      forEach(previousValues[FORM_SUBTEMPLATES_KEY], (subtemplate) => {
        forEach(subtemplate[FORM_ROLES_KEY], (role) => {
          previousRolesByUniqueId.set(role.uniqueId, role as ManageRolesFormType);
        });
      });

      const serverRoleIds = new Set<Id>();
      forEach(serverFormData[FORM_SUBTEMPLATES_KEY], (subtemplate) => {
        forEach(subtemplate[FORM_ROLES_KEY], (role) => {
          if (role.id !== undefined) {
            serverRoleIds.add(role.id);
          }
        });
      });

      const pendingDraftUniqueIds = new Set<string>();
      draftRoles.forEach((draft) => {
        const liveRole = previousRolesByUniqueId.get(draft.uniqueId);
        const isConfirmedByServer = liveRole?.id !== undefined && serverRoleIds.has(liveRole.id);
        if (isConfirmedByServer) {
          removeNewDraftRole(draft.uniqueId);
        } else {
          pendingDraftUniqueIds.add(draft.uniqueId);
        }
      });

      const currentDraftByUniqueId = new Map<string, ManageRolesFormType>();
      previousRolesByUniqueId.forEach((role, uniqueId) => {
        if (pendingDraftUniqueIds.has(uniqueId)) {
          currentDraftByUniqueId.set(uniqueId, role);
        } else {
          clearRoleData(uniqueId);
        }
      });

      const draftFormRoles = draftRoles
        .filter((draft) => pendingDraftUniqueIds.has(draft.uniqueId))
        .map((draft) => currentDraftByUniqueId.get(draft.uniqueId) ?? draft);

      const mergedFormData = mergeDraftRolesIntoForm(serverFormData, draftFormRoles);
      const nextFormData = applyPendingEditsToFormData(
        mergedFormData,
        manageRolesStoreApi.getState().unsavedEdits
      );

      const staleErrorPaths = getStaleFieldErrorPaths(
        methods.control._formState.errors,
        previousValues,
        nextFormData
      );

      trackerResetVersionRef.current += 1;
      methods.clearErrors(staleErrorPaths);
      methods.reset(nextFormData, { keepErrors: true });

      forEach(nextFormData[FORM_SUBTEMPLATES_KEY], (subtemplate) => {
        forEach(subtemplate[FORM_ROLES_KEY], (role) => {
          if (!pendingDraftUniqueIds.has(role.uniqueId)) {
            setRoleData(role.uniqueId, {
              activityCreator: profileByUniqueIdRef.current.get(role.uniqueId) ?? null,
              attachments: []
            });
          }
        });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods, manageRolesStoreApi, removeNewDraftRole]
  );

  useEffect(() => {
    syncFormPage(formData);
  }, [formData, syncFormPage]);

  return (
    <FormProvider {...methods}>
      <ManageRolesFormProviderInner currentPage={currentPage} trackerResetVersionRef={trackerResetVersionRef}>
        {children}
      </ManageRolesFormProviderInner>
    </FormProvider>
  );
};
